import { EXTRA_FEE_BASIS } from '../leasing-settings/leasing-settings.interface';
import type { ExtraFee, ExtraFeeBasis } from '../leasing-settings/leasing-settings.interface';
import type { ContractExtraFee } from './contract.interface';
import { roundToCents } from './contract-totals';

/** How each fee basis turns the contract value and the fee's rate into an amount (SWOOP-343). */
export const EXTRA_FEE_FORMULA: Record<ExtraFeeBasis, (contractValue: number, rate: number) => number> = {
  [EXTRA_FEE_BASIS.FIXED_PER_CONTRACT]: (_contractValue, rate) => rate,
  [EXTRA_FEE_BASIS.PERCENT_OF_CONTRACT_VALUE]: (contractValue, rate) => (contractValue * rate) / 100,
  [EXTRA_FEE_BASIS.PER_MILLE_OF_CONTRACT_VALUE]: (contractValue, rate) => (contractValue * rate) / 1000,
};

/** One fee's amount on a contract, rounded to the cent: 4‰ of 27,173 is 108.69. */
export function computeExtraFeeAmount(basis: ExtraFeeBasis, rate: number, contractValue: number): number {
  return roundToCents(EXTRA_FEE_FORMULA[basis](contractValue, rate));
}

/** Whether a fee applies: a mandatory fee always does; an optional one when ticked at its position. */
function isApplied(mandatory: boolean, applied: readonly boolean[] | undefined, index: number): boolean {
  return mandatory || applied?.[index] === true;
}

/**
 * The fees a new contract copies from its territory (SWOOP-346). Each territory fee becomes one contract fee
 * at the same position; `applied` is read by position (fees have no id), and `amount` is worked out on the
 * contract value, or 0 when the fee does not apply.
 */
export function buildContractExtraFees(
  territoryFees: readonly ExtraFee[],
  applied: readonly boolean[] | undefined,
  contractValue: number,
): ContractExtraFee[] {
  return territoryFees.map((fee, index) =>
    withAmount(
      {
        amount: 0,
        applied: isApplied(fee.mandatory, applied, index),
        basis: fee.basis,
        code: fee.code,
        mandatory: fee.mandatory,
        name: fee.name,
        rate: fee.amount,
      },
      contractValue,
    ),
  );
}

/**
 * The fees of a saved contract after an edit: the copies stay as they were drafted; only which optional
 * fees apply (when `applied` is given) and every amount change.
 */
export function recomputeContractExtraFees(
  stored: readonly ContractExtraFee[],
  applied: readonly boolean[] | undefined,
  contractValue: number,
): ContractExtraFee[] {
  return stored.map((fee, index) =>
    withAmount(
      { ...fee, applied: applied === undefined ? fee.applied : isApplied(fee.mandatory, applied, index) },
      contractValue,
    ),
  );
}

function withAmount(fee: ContractExtraFee, contractValue: number): ContractExtraFee {
  return { ...fee, amount: fee.applied ? computeExtraFeeAmount(fee.basis, fee.rate, contractValue) : 0 };
}
